import safeRedirect from './safeRedirect';
import { creditSessionIdOf, historyForServer, newCreditSessionId } from './creditSession';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { renewMobileSession, forgetMobileSession } from './auth/mobileSession';
import { OfflineSessionContext, useOfflineSession } from './offline/OfflineSessionContext';
import { hasOfflineConversations, clearAccountConversations, listConversations } from './offline/conversationRepository';
import { hasOfflineProjects, listProjects, clearAccountProjects } from './offline/projectRepository';
import { syncPendingProjectNotes, syncPendingProjectContexts } from './offline/projectSync';
import { clearAccountProjectFiles, listQueuedProjectFiles } from './offline/projectFileQueue';
import { cancelProjectFileUploads, syncPendingProjectFiles } from './offline/projectFileSync';
import { resolveOfflineSession, OFFLINE_ACCOUNT_KEY } from './offline/session';
import Header from './components/Header';
import { WhatsAppProvider } from './components/WhatsAppAccess';
import Chat from './components/Chat';
import Account from './components/Account';
import Footer from './components/Footer';
import JoinProject from './components/JoinProject';
import AuthPage from './components/Auth';
import DataDeletionPage from './components/DataDeletionPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import SubscriptionPage from './components/SubscriptionPage';
import MobileSubscriptionPage from './components/MobileSubscriptionPage';
import CrmPage from './components/CrmPage';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import BlogIndex from './components/BlogIndex';
import BlogPost from './components/BlogPost';
import LandingPage from './components/LandingPage';
import PublicLayout from './components/PublicLayout';
import AndroidOnboarding from './components/AndroidOnboarding';
import AndroidAuthLayout from './components/AndroidAuthLayout';
import ProjectPage from './components/ProjectPage';
import ProjectsDashboard from './components/ProjectsDashboard';
import Unsubscribe from './components/Unsubscribe';
import LocaleScope from './components/LocaleScope';
import { BLOG_LANGUAGES, HOME_LANGUAGES } from './content/publicLanguages';
import AdminPage from './admin/AdminPage';
// --- Diagnostic IA (isolated feature; remove this import + the route below
//     + the src/diagnostic/ folder to delete the whole thing) ---
// --- DiagIA admin platform (isolated; remove these 5 imports + the routes
//     below + the src/diagia/ folder to delete the whole platform) ---
import DiagiaShell from './diagia/DiagiaShell';
import DiagiaHome from './diagia/pages/DiagiaHome';
import DiagiaExample from './diagia/pages/DiagiaExample';
import DiagiaMine from './diagia/pages/DiagiaMine';
import DiagiaNew from './diagia/pages/DiagiaNew';
import DiagiaDocPage from './diagia/pages/DiagiaDocPage';
import DiagiaCampaigns from './diagia/pages/DiagiaCampaigns';
import DiagiaCampaignDetail from './diagia/pages/DiagiaCampaignDetail';
import DiagiaParticipants from './diagia/pages/DiagiaParticipants';
import DiagiaResults from './diagia/pages/DiagiaResults';
import DiagiaInvitations from './diagia/pages/DiagiaInvitations';
import InvitePublic from './diagia/pages/InvitePublic';
import { AUTH_API_URL, BACKEND_MAIN_URL, CRM_DEMO_MODE } from './config';
import { ActiveChatDomainProvider } from './ActiveChatDomainContext';
import './i18n';  // side-effect import: configures i18next

const DEFAULT_FALLBACK_PROMPT = `
  <Purpose/>
  You are an assistant helping agroecologists with question-answering tasks. Use the following pieces of retrieved information to answer the question.
  <Purpose/>
`;

const LoadingScreen = () => (
  <div className="container">
    <div className="auth-container">
      <p>Chargement...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ isAuthenticated, offlineReadOnly, children }) =>
  isAuthenticated ? children : <Navigate to={offlineReadOnly ? "/chat" : "/login"} replace />;

// Signed-in users skip /login — but to where they were headed (e.g. back to
// a project invitation), not always /chat, or the invite would be dropped
// the moment login flips isAuthenticated.
const RedirectAuthenticatedRoute = ({ isAuthenticated, children }) => {
  const location = useLocation();
  if (!isAuthenticated) return children;
  const next = safeRedirect(new URLSearchParams(location.search).get('redirect'));
  return <Navigate to={next || '/chat'} replace />;
};

const AppShell = ({ onLogout, children }) => {
  const { t } = useTranslation('workspace');
  const { offlineReadOnly, reconnect, sessionExpired, chatBackendUnavailable } = useOfflineSession();
  const content = <div className="container workspace-shell">
    <Header onLogout={onLogout} />
    {chatBackendUnavailable && <p role="status" style={{ padding: '10px 16px', background: '#edf3e9' }}>{t('localAuthOnly')}</p>}
    {offlineReadOnly && <div role="status" style={{ padding: '10px 16px', background: '#edf3e9', color: '#28432d' }}>
      {t(sessionExpired ? 'offlineSessionExpired' : 'offlineSessionReader')}
      {' '}<button type="button" onClick={reconnect}>{t(sessionExpired ? 'offlineSessionSignIn' : 'offlineSessionRetry')}</button>
    </div>}
    <main className="app-main">{children}</main>
    <Footer />
  </div>;
  return <ActiveChatDomainProvider><WhatsAppProvider enabled={!offlineReadOnly && !chatBackendUnavailable}>{content}</WhatsAppProvider></ActiveChatDomainProvider>;
};

const HomeRoute = ({ isAuthenticated, nativeAndroid, onboardingComplete, onComplete }) =>
  nativeAndroid && !onboardingComplete ? <AndroidOnboarding onComplete={onComplete} /> : isAuthenticated ? (
    <Navigate to="/chat" replace />
  ) : (
    nativeAndroid ? <Navigate to="/login" replace /> : <LandingPage />
  );

const App = () => {
  const { t } = useTranslation('workspace');
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem('token')));
  const [offlineReadOnly, setOfflineReadOnly] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionCheck, setSessionCheck] = useState(0);
  const sessionGeneration = useRef(0);
  const nativeAndroid = Capacitor.getPlatform() === 'android';
  const [onboardingComplete, setOnboardingComplete] = useState(() => {
    try { return localStorage.getItem('upya.android.onboarding.v1') === 'complete'; } catch { return false; }
  });
  const AuthLayout = nativeAndroid ? AndroidAuthLayout : PublicLayout;
  const chatBackendUnavailable = nativeAndroid && process.env.REACT_APP_CHAT_BACKEND_DISABLED === 'true';
  const [authResolved, setAuthResolved] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [responseComplete, setResponseComplete] = useState(false);
  const [marketAvailable, setMarketAvailable] = useState(false);
  const navigate = useNavigate();

  const socketRef = useRef(null);
  const defaultPromptRef = useRef(DEFAULT_FALLBACK_PROMPT);
  const currentStreamRef = useRef('');
  // Server-side chat memory is keyed by socket id, so any silent reconnect
  // (laptop sleep, network change, redeploy) starts a blank session. These
  // refs let the 'connect' handler re-push the visible history — without
  // them the agent "forgets" the conversation mid-thread.
  const messagesRef = useRef([]);
  const activeAgentRef = useRef('default');
  const creditSessionIdRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const handleLogout = useCallback(async () => {
    const accountId = localStorage.getItem('userId');
    const previousToken = localStorage.getItem('token');
    if (accountId && (nativeAndroid || window.indexedDB)) {
      try {
        const cached = nativeAndroid ? await listConversations(accountId) : [];
        const cachedProjects = nativeAndroid ? await listProjects(accountId) : [];
        const queuedFiles = await listQueuedProjectFiles(accountId);
        if ((cached.some((item) => item.pendingLocal) || cachedProjects.some((project) => project.notes?.some((note) => note.pending)) || queuedFiles.length) && !window.confirm(t('offlineSessionLogoutWarning'))) return;
        // Stop queued snapshot writers before the serialized purge.
        sessionGeneration.current += 1;
        localStorage.removeItem('userId');
        localStorage.removeItem('token');
        await cancelProjectFileUploads(accountId);
        if (nativeAndroid) {
          await clearAccountConversations(accountId);
          await clearAccountProjects(accountId);
        }
        await clearAccountProjectFiles(accountId);
      } catch {
        localStorage.setItem('userId', accountId);
        if (previousToken) localStorage.setItem('token', previousToken);
        window.alert(t('offlineSessionLogoutError'));
        return;
      }
    }
    sessionGeneration.current += 1;
    await forgetMobileSession();
    localStorage.removeItem(OFFLINE_ACCOUNT_KEY);
    setOfflineReadOnly(false);
    setSessionExpired(false);
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('sessionStart');
    setMessages([]);
    setDefaultPrompt('');
    setCustomPrompt('');
    setMarketAvailable(false);
    setIsLoading(false);
    setResponseComplete(false);
    currentStreamRef.current = '';
    setIsAuthenticated(false);
    navigate('/login');
  }, [navigate, nativeAndroid, t]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (nativeAndroid || !event.persisted) {
        return;
      }

      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setIsAuthenticated(false);
      setAuthResolved(true);
      navigate('/login');
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [navigate, nativeAndroid]);

  useEffect(() => {
    let cancelled = false;

    const generation = ++sessionGeneration.current;
    const verifyExistingSession = async () => {
      let token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      let error = nativeAndroid && navigator.onLine === false ? new Error('Offline') : null;
      if (nativeAndroid && !error) {
        try { token = await renewMobileSession(); } catch (failure) { error = failure; }
        if (cancelled || generation !== sessionGeneration.current) return;
      }
      if (token && !error) {
        try {
          await axios.get(`${AUTH_API_URL}/verify-token`, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
          });
          if (cancelled || generation !== sessionGeneration.current) return;
          if (nativeAndroid && userId) localStorage.setItem(OFFLINE_ACCOUNT_KEY, userId);
          setIsAuthenticated(true);
          setOfflineReadOnly(false);
          setSessionExpired(false);
          setAuthResolved(true);
          return;
        } catch (failure) { error = failure; }
      }
      const decision = await resolveOfflineSession({
        nativeAndroid, userId, provisionedUserId: localStorage.getItem(OFFLINE_ACCOUNT_KEY),
        error, hasCachedConversations: async (account) => (await hasOfflineConversations(account)) || hasOfflineProjects(account), hasVisibleMessages: messagesRef.current.length > 0,
      });
      if (cancelled || generation !== sessionGeneration.current) return;
      setIsAuthenticated(false);
      setOfflineReadOnly(decision.allowed);
      setSessionExpired(decision.sessionExpired);
      if (!decision.allowed && !nativeAndroid) {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
      }
      setAuthResolved(true);
    };
    verifyExistingSession();
    return () => { cancelled = true; };
  }, [nativeAndroid, sessionCheck]);

  useEffect(() => {
    if (!offlineReadOnly) return;
    setIsLoading(false);
    setResponseComplete(false);
    currentStreamRef.current = '';
  }, [offlineReadOnly]);

  useEffect(() => {
    if (!nativeAndroid || !authResolved || !isAuthenticated || offlineReadOnly || chatBackendUnavailable) return;
    const sync = () => {
      const account = localStorage.getItem('userId');
      if (account && navigator.onLine !== false) {
        syncPendingProjectNotes(account).catch(() => { /* Notes stay pending for the next retry. */ });
        syncPendingProjectContexts(account).catch(() => { /* Context stays pending for the next retry. */ });
        syncPendingProjectFiles(account).catch(() => { /* Files stay queued for the next retry. */ });
      }
    };
    sync();
    window.addEventListener('online', sync);
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.removeEventListener('online', sync); document.removeEventListener('visibilitychange', onVisible); };
  }, [nativeAndroid, authResolved, isAuthenticated, offlineReadOnly, chatBackendUnavailable]);

  useEffect(() => {
    if (!nativeAndroid) return undefined;
    const check = () => { setSessionCheck((value) => value + 1); };
    const offline = () => {
      // Stop remote work immediately; the verifier decides whether cached reader access exists.
      setIsAuthenticated(false);
      setOfflineReadOnly(Boolean(localStorage.getItem('userId') && localStorage.getItem('userId') === localStorage.getItem(OFFLINE_ACCOUNT_KEY)));
      check();
    };
    const visible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('online', check);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('online', check);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [nativeAndroid]);

  useEffect(() => {
    if (!isAuthenticated || !authResolved || offlineReadOnly) {
      return undefined;
    }

    const refresh = async () => {
      if (nativeAndroid) {
        try { await renewMobileSession(); } catch { /* Revalidation preserves cached reading during outages. */ }
        setSessionCheck((value) => value + 1);
        return;
      }
      const token = localStorage.getItem('token');

      if (!token) {
        return;
      }

      try {
        const res = await fetch(`${AUTH_API_URL}/refresh-token`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.token) {
            localStorage.setItem('token', data.token);
          }
        } else {
          if (nativeAndroid) setSessionCheck((value) => value + 1);
          else handleLogout();
        }
      } catch (_error) {
        // Ignore transient network errors and keep current session state.
      }
    };

    const intervalId = setInterval(refresh, (nativeAndroid ? 5 : 45) * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [handleLogout, isAuthenticated, authResolved, offlineReadOnly, nativeAndroid]);

  useEffect(() => {
    if (!isAuthenticated || !authResolved || offlineReadOnly || chatBackendUnavailable) {
      return undefined;
    }

    const socket = io(BACKEND_MAIN_URL, {
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    socketRef.current = socket;
    // A dropped socket is not a lost session: Android backgrounds the WebView
    // for the system file picker and the transport closes. Switching to the
    // reader there unmounted the project page and lost the picked file.
    const backgrounded = () => document.visibilityState === 'hidden';
    let dropped = false;

    socket.on('connect', () => {
      console.log('Connecte au serveur avec ID :', socket.id);
      if (dropped && nativeAndroid && !backgrounded()) {
        // Re-check the session after an outage: an expired token opens the reader.
        dropped = false;
        setSessionCheck((value) => value + 1);
      }
      // Reconnect = new socket id = blank server-side memory. Re-push the
      // conversation so follow-up questions keep their context. The finance
      // guided assessment is not tied to the socket: it continues (#124).
      const currentMessages = messagesRef.current;
      if (currentMessages.length > 0) {
        let userId = null;
        try {
          const token = localStorage.getItem('token');
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.userId || payload.user_id || payload.sub;
        } catch (_error) {
          userId = null;
        }
        socket.emit('restore_history', {
          agent_id: activeAgentRef.current,
          messages: historyForServer(currentMessages),
          user_id: userId,
        });
      }
    });
    socket.on('agent_capabilities', (data) => setMarketAvailable(data?.market === true));
    const enterNativeReader = () => {
      if (!nativeAndroid) return;
      setIsLoading(false);
      setResponseComplete(false);
      setIsAuthenticated(false);
      setSessionExpired(false);
      setOfflineReadOnly(Boolean(localStorage.getItem('userId') && localStorage.getItem('userId') === localStorage.getItem(OFFLINE_ACCOUNT_KEY)));
    };
    // Only real network loss opens the reader at once; otherwise socket.io retries.
    socket.on('connect_error', (error) => {
      setMarketAvailable(false);
      dropped = true;
      if (navigator.onLine === false) enterNativeReader();
      console.error('Erreur de connexion :', error);
    });
    socket.on('disconnect', (reason) => {
      setMarketAvailable(false);
      if (reason !== 'io client disconnect') {
        dropped = true;
        if (navigator.onLine === false) enterNativeReader();
      }
      console.error('Deconnecte du serveur :', reason);
    });
    // Every retry failed: the backend is down, unless the app was in the background.
    const reconnectFailed = () => { if (!backgrounded()) enterNativeReader(); };
    const resume = () => { if (nativeAndroid && !backgrounded() && !socket.connected) socket.connect(); };
    socket.io.on('reconnect_failed', reconnectFailed);
    document.addEventListener('visibilitychange', resume);

    socket.on('default_prompt', (data) => {
      const prompt = data.prompt || defaultPromptRef.current;
      defaultPromptRef.current = prompt;
      setDefaultPrompt(prompt);
      setCustomPrompt(prompt);
    });

    socket.on('stream_token', (data) => {
      const token = data.token || '';
      currentStreamRef.current += token;
      setResponseComplete(false);

      setMessages((previousMessages) => {
        const updated = [...previousMessages];

        if (updated.length === 0 || updated[updated.length - 1].isUser) {
          return [...updated, { content: token, isUser: false }];
        }

        const lastMessage = updated[updated.length - 1];
        return [...updated.slice(0, -1), { content: lastMessage.content + token, isUser: false }];
      });
    });

    socket.on('final_answer', (data) => {
      const assistantMessage = {
        content: data.answer,
        isUser: false,
        creditQuestionnaire: data.credit_questionnaire || null,
        creditFlowStatus: data.credit_flow_status || null,
        riskSummary: data.risk_summary || null,
        repaymentSchedule: data.repayment_schedule || null,
        // Saved with the conversation: reopening it resumes this assessment (see creditSession.js).
        ...((data.credit_questionnaire || data.credit_flow_status) && creditSessionIdRef.current
          ? { creditSessionId: creditSessionIdRef.current } : {}),
      };

      setMessages((previousMessages) => {
        const updated = [...previousMessages];

        if (updated.length > 0 && !updated[updated.length - 1].isUser) {
          updated[updated.length - 1] = assistantMessage;
          return updated;
        }

        return [...updated, assistantMessage];
      });

      currentStreamRef.current = '';
      setIsLoading(false);
      setResponseComplete(true);
    });

    socket.on('answer', (data) => {
      setMessages((previousMessages) => [...previousMessages, { content: data.answer, isUser: false }]);
      setIsLoading(false);
      setResponseComplete(true);
    });

    socket.on('error', (data) => {
      console.error('Erreur recue du serveur :', data.error);
      setMessages((previousMessages) => [...previousMessages, { content: `Error: ${data.error}`, isUser: false }]);
      currentStreamRef.current = '';
      setIsLoading(false);
      setResponseComplete(true);
    });

    socket.emit('get_default_prompt');

    return () => {
      setMarketAvailable(false);
      document.removeEventListener('visibilitychange', resume);
      socket.io.off('reconnect_failed', reconnectFailed);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, authResolved, offlineReadOnly, nativeAndroid, chatBackendUnavailable]);

  const handlePromptUpdate = (newPrompt) => {
    if (offlineReadOnly) return;
    setCustomPrompt(newPrompt);
    socketRef.current?.emit('update_prompt', { custom_prompt: newPrompt });
  };

  const handleQuestionSubmit = (question, options = {}) => {
    if (chatBackendUnavailable || offlineReadOnly || !socketRef.current) {
      return;
    }

    if (!socketRef.current.connected) {
      socketRef.current.connect();
      setMessages((previousMessages) => [
        ...previousMessages,
        {
          content: "Error: connexion au serveur interrompue. Rechargez la page puis réessayez.",
          isUser: false,
        },
      ]);
      setIsLoading(false);
      setResponseComplete(true);
      return;
    }

    const token = localStorage.getItem('token');
    let userId = null;
    const agentId = options.agentId || 'default';
    activeAgentRef.current = agentId;
    const userMessage = options.userMessage || question;

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userId = payload.userId || payload.user_id || payload.sub;
      } catch (error) {
        console.warn('Could not decode JWT:', error);
      }
    }

    currentStreamRef.current = '';
    setIsLoading(true);
    setResponseComplete(false);
    setMessages((previousMessages) => [...previousMessages, { content: userMessage, isUser: true }]);

    // User location (set via the weather widget) — lets the backend fetch
    // real weather when the question is weather-sensitive.
    let userLat = null;
    let userLon = null;
    try {
      const savedLoc = JSON.parse(localStorage.getItem('upya_weather_location') || 'null');
      if (savedLoc && typeof savedLoc.lat === 'number' && typeof savedLoc.lon === 'number') {
        userLat = savedLoc.lat;
        userLon = savedLoc.lon;
      }
    } catch { /* no location set */ }

    socketRef.current.emit('ask', {
      question,
      agent_id: agentId,
      custom_prompt: customPrompt,
      user_id: userId,
      // The backend derives identity from this verified JWT; the raw
      // user_id above is ignored server-side (kept for Azure compat).
      token: token || null,
      credit_response: options.creditResponse || null,
      credit_session_id: agentId === 'credit_agricole'
        ? (creditSessionIdRef.current ||= newCreditSessionId())
        : null,
      project_id: options.projectId || null,
      conversation_id: options.conversationId || null,
      project_instructions: options.projectInstructions || null,
      attached_files_text: options.attachedFilesText || null,
      // Flash is the product default; the user opts into detailed.
      answer_mode: options.answerMode || 'flash',
      user_lat: userLat,
      user_lon: userLon,
    });
  };

  const handleConversationLoad = (conversationMessages, agentId = 'default') => {
    activeAgentRef.current = agentId;
    setMessages(conversationMessages);
    if (offlineReadOnly || !socketRef.current) {
      return;
    }

    activeAgentRef.current = agentId;
    setMessages(conversationMessages);

    const token = localStorage.getItem('token');
    let userId = null;

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userId = payload.userId || payload.user_id || payload.sub;
      } catch (_error) {
        userId = null;
      }
    }

    // Another conversation on screen: the next guided assessment answer belongs to
    // that conversation's assessment (its id travels with its Finance answers), so a
    // reload or an outage never loses it; a conversation without one starts anew.
    creditSessionIdRef.current = creditSessionIdOf(conversationMessages);
    socketRef.current.emit('restore_history', {
      agent_id: agentId,
      messages: historyForServer(conversationMessages),
      user_id: userId,
    });
  };

  // Push the current visible chat history into the websocket's server-side
  // memory for an agent. Vision/PlantNet/Lab go over HTTP and never touch the
  // websocket history, so after one of those answers we resync — otherwise the
  // default agent forgets the photo turn on the next follow-up question.
  const handleChatHistorySync = (chatMessages, agentId = 'default') => {
    if (offlineReadOnly || !socketRef.current) return;

    activeAgentRef.current = agentId;

    const token = localStorage.getItem('token');
    let userId = null;
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userId = payload.userId || payload.user_id || payload.sub;
      } catch (_error) {
        userId = null;
      }
    }

    socketRef.current.emit('restore_history', {
      agent_id: agentId,
      messages: chatMessages,
      user_id: userId,
    });
  };

  const handleAgentSessionReset = (agentId = 'default') => {
    if (offlineReadOnly || !socketRef.current) {
      return;
    }

    setMessages([]);
    setIsLoading(false);
    setResponseComplete(false);
    if (agentId === 'credit_agricole') creditSessionIdRef.current = null;
    socketRef.current.emit('reset_agent_session', { agent_id: agentId });
  };

  const handleAuthSuccess = () => {
    sessionGeneration.current += 1;
    if (nativeAndroid && localStorage.getItem('userId')) localStorage.setItem(OFFLINE_ACCOUNT_KEY, localStorage.getItem('userId'));
    setOfflineReadOnly(false);
    setSessionExpired(false);
    setMessages([]);
    localStorage.setItem('sessionStart', Date.now().toString());
    setIsAuthenticated(true);
    setAuthResolved(true);
    navigate('/chat');
  };

  if (!authResolved) {
    return <LoadingScreen />;
  }

  const revalidate = () => {
    if (!nativeAndroid) return;
    setIsAuthenticated(false);
    setOfflineReadOnly(Boolean(localStorage.getItem('userId') && localStorage.getItem('userId') === localStorage.getItem(OFFLINE_ACCOUNT_KEY)));
    setSessionCheck((value) => value + 1);
  };
  const reconnect = () => {
    if (sessionExpired) navigate('/login');
    else setSessionCheck((value) => value + 1);
  };
  const completeOnboarding = () => {
    try { localStorage.setItem('upya.android.onboarding.v1', 'complete'); } catch { /* Continue even if local storage is unavailable. */ }
    setOnboardingComplete(true);
    navigate((isAuthenticated || offlineReadOnly) ? '/chat' : '/login', { replace: true });
  };
  return (
    <OfflineSessionContext.Provider value={{ offlineReadOnly, chatBackendUnavailable, userId: localStorage.getItem('userId'), sessionExpired, reconnect, revalidate }}>
    <Routes>
      <Route path="/" element={<HomeRoute isAuthenticated={isAuthenticated || offlineReadOnly} nativeAndroid={nativeAndroid} onboardingComplete={onboardingComplete} onComplete={completeOnboarding} />} />

      <Route
        path="/login"
        element={
          <RedirectAuthenticatedRoute isAuthenticated={isAuthenticated}>
            <AuthLayout>
              <AuthPage onAuthSuccess={handleAuthSuccess} />
            </AuthLayout>
          </RedirectAuthenticatedRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <RedirectAuthenticatedRoute isAuthenticated={isAuthenticated}>
            <AuthLayout>
              <ForgotPassword />
            </AuthLayout>
          </RedirectAuthenticatedRoute>
        }
      />

      <Route
        path="/reset-password"
        element={
          <RedirectAuthenticatedRoute isAuthenticated={isAuthenticated}>
            <AuthLayout>
              <ResetPassword />
            </AuthLayout>
          </RedirectAuthenticatedRoute>
        }
      />

      <Route
        path="/blog"
        element={
          <PublicLayout>
            <BlogIndex />
          </PublicLayout>
        }
      />

      <Route
        path="/blog/:slug"
        element={
          <PublicLayout>
            <BlogPost />
          </PublicLayout>
        }
      />

      {/* The same public pages in other languages, each at its own URL (/fr, /fr/blog, /fr/blog/<slug>):
          search engines and AI crawlers only index what has a URL. */}
      {HOME_LANGUAGES.map((lang) => (
        <Fragment key={lang}>
          <Route path={`/${lang}`} element={<LocaleScope lang={lang}><HomeRoute isAuthenticated={isAuthenticated} /></LocaleScope>} />
          {BLOG_LANGUAGES.includes(lang) && <>
            <Route path={`/${lang}/blog`} element={<LocaleScope lang={lang}><PublicLayout><BlogIndex /></PublicLayout></LocaleScope>} />
            <Route path={`/${lang}/blog/:slug`} element={<LocaleScope lang={lang}><PublicLayout><BlogPost /></PublicLayout></LocaleScope>} />
          </>}
        </Fragment>
      ))}

      <Route
        path="/terms"
        element={
          <PublicLayout>
            <div className="legal-page">
              <TermsOfUse />
            </div>
          </PublicLayout>
        }
      />

      <Route
        path="/privacy"
        element={
          <PublicLayout>
            <div className="legal-page">
              <PrivacyPolicy />
            </div>
          </PublicLayout>
        }
      />

      <Route
        path="/data-deletion"
        element={
          <PublicLayout>
            <div className="legal-page">
              <DataDeletionPage />
            </div>
          </PublicLayout>
        }
      />

      <Route
        path="/chat"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated || offlineReadOnly}>
            <AppShell onLogout={handleLogout}>
              <Chat
                offlineReadOnly={offlineReadOnly}
                messages={messages}
                marketAvailable={marketAvailable}
                defaultPrompt={defaultPrompt}
                customPrompt={customPrompt}
                onPromptUpdate={handlePromptUpdate}
                onQuestionSubmit={handleQuestionSubmit}
                onConversationLoad={handleConversationLoad}
                onAgentSessionReset={handleAgentSessionReset}
                onChatHistorySync={handleChatHistorySync}
                isLoading={isLoading}
                responseComplete={responseComplete}
                onLogout={handleLogout}
              />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated || offlineReadOnly} offlineReadOnly={offlineReadOnly}>
            <AppShell onLogout={handleLogout}>
              <ProjectsDashboard onLogout={handleLogout} />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated || offlineReadOnly} offlineReadOnly={offlineReadOnly}>
            <AppShell onLogout={handleLogout}>
              <ProjectPage
                messages={messages}
                marketAvailable={marketAvailable}
                onQuestionSubmit={handleQuestionSubmit}
                onConversationLoad={handleConversationLoad}
                onAgentSessionReset={handleAgentSessionReset}
                onChatHistorySync={handleChatHistorySync}
                isLoading={isLoading}
                responseComplete={responseComplete}
                onLogout={handleLogout}
              />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/account"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} offlineReadOnly={offlineReadOnly}>
            <AppShell onLogout={handleLogout}>
              <Account />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/upgrade"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} offlineReadOnly={offlineReadOnly}>
            <AppShell onLogout={handleLogout}>
              {Capacitor.getPlatform() === 'android' ? <MobileSubscriptionPage /> : <SubscriptionPage />}
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route path="/crm" element={<ProtectedRoute isAuthenticated={isAuthenticated || CRM_DEMO_MODE} offlineReadOnly={offlineReadOnly}><AppShell onLogout={handleLogout}><CrmPage /></AppShell></ProtectedRoute>} />

      {/* The link at the bottom of the team's group emails (public, no login). */}
      <Route path="/unsubscribe" element={<PublicLayout><Unsubscribe /></PublicLayout>} />

      {/* The team's admin page: statistics, users, plans, follow-up. The backend only answers
          the emails in CRM_ADMIN_EMAILS; everyone else sees a "team only" notice. */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} offlineReadOnly={offlineReadOnly}>
            <AppShell onLogout={handleLogout}>
              <AdminPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* DiagIA admin platform — auth required (the shell loads identity and
          gates the admin menus). Remove these Routes to delete the platform. */}
      <Route path="/diagia" element={offlineReadOnly ? <Navigate to="/chat" replace /> : <DiagiaShell />}>
        <Route index element={<DiagiaHome />} />
        <Route path="example" element={<DiagiaExample />} />
        <Route path="new" element={<DiagiaNew />} />
        <Route path="diagnostics" element={<DiagiaMine />} />
        <Route path="diagnostics/:id" element={<DiagiaDocPage />} />
        <Route path="campaigns" element={<DiagiaCampaigns />} />
        <Route path="campaigns/:id" element={<DiagiaCampaignDetail />} />
        <Route path="participants" element={<DiagiaParticipants />} />
        <Route path="results" element={<DiagiaResults />} />
        <Route path="invitations" element={<DiagiaInvitations />} />
      </Route>

      {/* Public invite landing — no DiagIA shell, no auth required */}
      <Route path="/invite/:token" element={<InvitePublic />} />
      {/* Project invitation links (#72). Open to signed-out visitors: the page
          itself sends them to sign up / log in and brings them back. */}
      <Route path="/join/:token" element={<PublicLayout><JoinProject isAuthenticated={isAuthenticated} /></PublicLayout>} />

      <Route path="*" element={<Navigate to={(isAuthenticated || offlineReadOnly) ? '/chat' : '/'} replace />} />
    </Routes>
    </OfflineSessionContext.Provider>
  );
};

export default App;
